/**
 * Life4Earth V2 — Notification sound playback.
 *
 * Plays an audio chime when a new chat message arrives and the browser tab
 * is hidden (document.hidden === true).  Gated by the user preference
 * stored in localStorage (`l4e_notification_sound`).
 *
 * To replace the default chime with a custom sound, drop an MP3 or WAV file
 * at  public/sounds/message-notification.mp3  (or .wav) and update SOUND_URL.
 */

const SOUND_URL = `${process.env.PUBLIC_URL || ''}/sounds/message-notification.wav`;

let audioElement: HTMLAudioElement | null = null;

/** Lazy-create the <audio> element (reusable across calls). */
function getAudio(): HTMLAudioElement {
  if (!audioElement) {
    audioElement = new Audio(SOUND_URL);
    audioElement.volume = 0.5;
  }
  return audioElement;
}

/**
 * Play the notification chime if:
 *   1. The browser tab is hidden (document.hidden === true)
 *   2. The user has not disabled notification sounds
 *
 * Silently fails if the browser blocks autoplay.
 */
export function playNotificationSound(): void {
  try {
    if (!document.hidden) return;
    if (!isNotificationSoundEnabled()) return;

    const audio = getAudio();
    // Reset to start in case a previous play is still going
    audio.currentTime = 0;
    audio.play().catch(() => {
      // Browser autoplay policy blocked it — silent fail
    });
  } catch {
    // Guard against SSR / test environments
  }
}

/* ------------------------------------------------------------------ */
/*  User preference (localStorage)                                     */
/* ------------------------------------------------------------------ */

const LS_KEY = 'l4e_notification_sound';

/** Check if notification sound is enabled (defaults to true). */
export function isNotificationSoundEnabled(): boolean {
  try {
    const val = window?.localStorage?.getItem(LS_KEY);
    // Default is ON — only OFF if explicitly set to 'false'
    return val !== 'false';
  } catch {
    return true;
  }
}

/** Set the notification sound preference. */
export function setNotificationSoundEnabled(enabled: boolean): void {
  try {
    window?.localStorage?.setItem(LS_KEY, String(enabled));
  } catch {
    // ignore
  }
}
